import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspaceContext } from '../providers/WorkspaceProvider';
import { usePreviewContext } from '../providers/PreviewProvider';
import ChatWorkspace from '../chat';
import { HistoryProvider } from '../../../components/editor/context/HistoryContext';
import { ResumeProvider } from '../../../components/editor/context/ResumeContext';
import { AutosaveProvider } from '../../../components/editor/context/AutosaveContext';
import ResumeEditor from '../../../components/editor/ResumeEditor';
import CoverLetterEditor from '../../../components/editor/CoverLetterEditor';
import { useAppContext } from '../../../context/AppContext';
import AutosaveIndicator from '../../../components/common/AutosaveIndicator';
import ATSScreen from '../../../components/ats/ATSScreen';
import JDMatchScreen from '../../../components/jd/JDMatchScreen';
import TemplateGallery from '../../../components/gallery';

export default function CenterWorkspace() {
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = React.useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    activeTab,
    mobileTab,
    theme,
    authToken,
    setCredits,
  } = useWorkspaceContext();

  const {
    accentColor,
    setAccentColor,
    fontScale,
    setFontScale,
    template,
    handleTemplateChange,
    fontFamily,
    setFontFamily,
    credits,
    userDomain,
    setShowDomainSelector,
    coverTemplate,
    setCoverTemplate,
    activeCoverLetter,
    setInput,
    textareaRef,
    handleCreateCoverLetter,
    atsScore,
  } = usePreviewContext();

  const {
    resumeData,
    setResumeData,
    autosaveStatus,
  } = useAppContext();

  // JD Match state
  const [jdText, setJdText] = React.useState('');
  const [jdResult, setJdResult] = React.useState(null);
  const [jdLoading, setJdLoading] = React.useState(false);
  const [jdError, setJdError] = React.useState('');
  const [varCreating, setVarCreating] = React.useState(false);
  const [varCreated, setVarCreated] = React.useState(false);
  const [deepAnalysis, setDeepAnalysis] = React.useState(false);

  // handleSendPrompt
  const handleSendPrompt = React.useCallback((promptText) => {
    if (setInput) {
      setInput(promptText);
      const id = resumeData?.id || 'master';
      navigate(`/app/resume/${id}/chat`);
      setTimeout(() => textareaRef?.current?.focus(), 100);
    }
  }, [setInput, navigate, resumeData, textareaRef]);

  // handleJDAnalyze
  const handleJDAnalyze = React.useCallback(async () => {
    if (!jdText.trim() || jdLoading) return;
    setJdLoading(true);
    setJdError('');
    setJdResult(null);
    try {
      const res = await fetch('/api/jd/match', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ jd_text: jdText, deep_analysis: deepAnalysis }),
      });
      if (res.status === 402 || res.status === 403) { setJdError('Insufficient credits.'); return; }
      if (!res.ok) { setJdError('Analysis failed. Please try again.'); return; }
      const data = await res.json();
      setJdResult(data);
      if (data.credits_remaining !== undefined && data.credits_remaining !== null) {
        setCredits(data.credits_remaining);
      }
    } catch (err) {
      setJdError(err.message || 'Analysis failed.');
    } finally {
      setJdLoading(false);
    }
  }, [authToken, jdText, jdLoading, deepAnalysis, setCredits]);

  // handleJDOptimize
  const handleJDOptimize = React.useCallback(() => {
    if (!jdText.trim()) return;
    handleSendPrompt(`Optimize my resume for this job description:\n\n${jdText}`);
  }, [jdText, handleSendPrompt]);

  const isTabInChatList = ['chat', 'ai', 'cover', 'mock_interview', 'career_roadmap'].includes(activeTab);
  const isHidden = ['home', 'assets_vault', 'tracker', 'career_memory', 'profile'].includes(activeTab) ||
                   (!isMobile && ['ats', 'jd', 'jd_match'].includes(activeTab));
  const widthClass = activeTab === 'templates' ? 'md:w-[600px]' : 'md:flex-1 md:min-w-[480px]';

  return (
    <div
      className={`${
        mobileTab === 1 && !isHidden ? 'flex flex-1' : 'hidden'
      } ${isHidden ? 'hidden' : 'md:flex ' + widthClass} shrink-0 flex flex-col min-w-0 z-10 ${
        theme === 'dark' ? 'bg-black' : 'bg-white'
      }`}
    >
      {/* AI Chat workspace */}
      <div className={`${isTabInChatList ? 'flex' : 'hidden'} flex-1 flex-col min-h-0 relative ${theme === 'dark' ? 'bg-black' : 'bg-white'}`}>
        <ChatWorkspace />
      </div>

      {/* Structured Resume/Cover Letter Editor Panel */}
      {activeCoverLetter ? (
        <div className={activeTab === 'editor' ? 'flex flex-col flex-1 min-h-0' : 'hidden'}>
          <CoverLetterEditor />
        </div>
      ) : (
        resumeData && (
          <HistoryProvider onChange={setResumeData}>
            <ResumeProvider initialResume={resumeData} onUpdateParent={setResumeData}>
              <AutosaveProvider authToken={authToken}>
                {/* Autosave status indicator in the editor toolbar area */}
                <div style={{
                  position: 'absolute', top: 12, right: 16, zIndex: 20,
                  display: activeTab === 'editor' ? 'block' : 'none',
                }}>
                  <AutosaveIndicator status={autosaveStatus} theme={theme} />
                </div>
                <div className={activeTab === 'editor' ? 'flex flex-col flex-1 min-h-0 relative' : 'hidden'}>
                  <ResumeEditor />
                </div>
              </AutosaveProvider>
            </ResumeProvider>
          </HistoryProvider>
        )
      )}

      {/* ATS Screen */}
      {activeTab === 'ats' && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden w-full">
          <ATSScreen
            atsScore={atsScore}
            resumeData={resumeData}
            theme={theme}
            onImprove={(txt) => {
              if (txt && typeof txt === 'string') {
                handleSendPrompt('Fix this ATS issue in my resume: ' + txt);
              } else {
                handleSendPrompt('Help me improve my ATS score. What should I fix first?');
              }
            }}
          />
        </div>
      )}

      {/* JD Matcher Screen */}
      {['jd_match', 'jd'].includes(activeTab) && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden w-full">
          <JDMatchScreen
            jdText={jdText}
            setJdText={setJdText}
            jdResult={jdResult}
            setJdResult={setJdResult}
            jdLoading={jdLoading}
            jdError={jdError}
            setJdError={setJdError}
            varCreating={varCreating}
            varCreated={varCreated}
            deepAnalysis={deepAnalysis}
            setDeepAnalysis={setDeepAnalysis}
            onAnalyze={handleJDAnalyze}
            onOptimize={handleJDOptimize}
            onCreateVariant={async () => {
              setVarCreating(true);
              try {
                const res = await fetch('/api/variants', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                  body: JSON.stringify({ name: 'JD Tailored Version', jd_text: jdText }),
                });
                if (res.ok) {
                  const d = await res.json();
                  if (d.credits_remaining !== undefined) setCredits(d.credits_remaining);
                  setVarCreated(true);
                  if (d.variant?.id) {
                    navigate(`/app/resume/${d.variant.id}/chat`);
                  }
                }
              } catch {}
              finally { setVarCreating(false); }
            }}
            onCoverLetter={async () => {
              const newCl = await handleCreateCoverLetter();
              if (newCl) {
                navigate(`/app/cover-letter/${newCl.id}`);
              }
            }}
            theme={theme}
          />
        </div>
      )}

      {/* Templates Gallery Screen */}
      {activeTab === 'templates' && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden w-full">
          <TemplateGallery
            selectedTemplate={template}
            setTemplate={handleTemplateChange}
            accentColor={accentColor}
            setAccentColor={setAccentColor}
            fontScale={fontScale}
            setFontScale={setFontScale}
            fontFamily={fontFamily}
            setFontFamily={setFontFamily}
            authToken={authToken}
            onTemplateRecommend={handleTemplateChange}
            credits={credits}
            onSendToChat={handleSendPrompt}
            resumeData={resumeData}
            userDomain={userDomain}
            domainLabel={resumeData?._domain ? (resumeData._domain.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())) : null}
            onChangeDomain={() => setShowDomainSelector(true)}
            theme={theme}
            coverTemplate={coverTemplate}
            setCoverTemplate={setCoverTemplate}
            activeCoverLetter={activeCoverLetter}
          />
        </div>
      )}

      {/* Typography settings panel (Legacy Text tab) */}
      <div className={`${activeTab === 'text' ? 'block' : 'hidden'} flex-1 overflow-y-auto p-5 space-y-6`}>
        <div className="border-b border-gray-200 pb-3">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <span>🔤</span> Typography & Colors
          </h3>
          <p className="text-[11px] text-gray-500 mt-1">Customize accent colors and font sizes of your resume.</p>
        </div>

        <div className="space-y-4">
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Accent Color</p>
          <div className="grid grid-cols-5 gap-2.5">
            {[
              { color: '#1a1a2e', label: 'Navy' },
              { color: '#0f4c75', label: 'Ocean' },
              { color: '#0f172a', label: 'Charcoal' },
              { color: '#134e4a', label: 'Forest' },
              { color: '#1e3a5f', label: 'Midnight' },
              { color: '#7C3AED', label: 'Violet' },
              { color: '#2563EB', label: 'Blue' },
              { color: '#059669', label: 'Emerald' },
              { color: '#b91c1c', label: 'Crimson' },
              { color: '#0369a1', label: 'Steel' },
              { color: '#8c6239', label: 'Warm Brown' },
              { color: '#92400e', label: 'Caramel' },
              { color: '#9c4221', label: 'Rust' },
              { color: '#b45309', label: 'Amber' },
              { color: '#6b5c3e', label: 'Mocha' }
            ].map((c) => (
              <button
                key={c.color}
                onClick={() => setAccentColor(c.color)}
                title={c.label}
                className="w-full aspect-square rounded-lg border-none cursor-pointer transition-all relative flex items-center justify-center"
                style={{
                  background: c.color,
                  boxShadow: accentColor === c.color ? `0 0 0 2px ${theme === 'dark' ? '#fff' : '#000'}, 0 4px 12px ${c.color}50` : 'none',
                  transform: accentColor === c.color ? 'scale(1.08)' : 'scale(1)',
                }}
              />
            ))}
          </div>

          <div style={{ marginTop: 24 }}>
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3">Font Scale</p>
            <input
              type="range"
              min="0.8"
              max="1.3"
              step="0.01"
              value={fontScale}
              onChange={e => setFontScale?.(parseFloat(e.target.value))}
              className="w-full cursor-pointer h-1.5 bg-gray-200 rounded-lg appearance-none"
              style={{ accentColor }}
            />
            <div className="flex justify-between text-[11px] text-gray-500 mt-2">
              <span>A</span>
              <span className="font-bold text-gray-900">{Math.round(fontScale * 100)}%</span>
              <span className="text-sm font-medium">A</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
